/**
 * React hook for managing job selection state
 *
 * Provides multi-select functionality for job checkboxes with:
 * - Selection state management
 * - Select all / deselect all operations
 * - Toggle individual job selection
 * - Check if a job is selected
 */

import { useState, useCallback, useMemo } from 'react';
import { Job } from '../types/job';

/**
 * Result from useJobSelection hook
 */
export interface UseJobSelectionResult {
  selectedJobIds: Set<string>;
  selectAll: () => void;
  deselectAll: () => void;
  toggleJob: (jobId: string) => void;
  selectJobsByIds: (jobIds: string[]) => void;
  isSelected: (jobId: string) => boolean;
  selectedCount: number;
  hasSelection: boolean;
}

/**
 * Custom hook for managing job selection state in multi-select UI
 *
 * @param jobs - Array of jobs available for selection
 * @returns Object with selection state and control functions
 *
 * @example
 * const { selectedJobIds, selectAll, deselectAll, toggleJob, isSelected } = useJobSelection(jobs);
 *
 * // Render checkbox
 * <input
 *   type="checkbox"
 *   checked={isSelected(job.job_id)}
 *   onChange={() => toggleJob(job.job_id)}
 * />
 *
 * // Select all button
 * <button onClick={selectAll}>Select All</button>
 */
export function useJobSelection(jobs: Job[]): UseJobSelectionResult {
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());

  /**
   * Select all jobs in the current list
   */
  const selectAll = useCallback(() => {
    const allJobIds = new Set(jobs.map((job) => job.job_id));
    setSelectedJobIds(allJobIds);
  }, [jobs]);

  /**
   * Deselect all jobs
   */
  const deselectAll = useCallback(() => {
    setSelectedJobIds(new Set());
  }, []);

  /**
   * Toggle selection for a single job
   */
  const toggleJob = useCallback((jobId: string) => {
    setSelectedJobIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  }, []);

  /**
   * Select specific jobs by their IDs
   * Useful for "Select Failed" or other filtered selections
   */
  const selectJobsByIds = useCallback((jobIds: string[]) => {
    setSelectedJobIds(new Set(jobIds));
  }, []);

  /**
   * Check if a job is currently selected
   */
  const isSelected = useCallback(
    (jobId: string): boolean => {
      return selectedJobIds.has(jobId);
    },
    [selectedJobIds]
  );

  /**
   * Count of selected jobs
   */
  const selectedCount = useMemo(() => selectedJobIds.size, [selectedJobIds]);

  /**
   * Whether any jobs are selected
   */
  const hasSelection = useMemo(() => selectedJobIds.size > 0, [selectedJobIds]);

  return {
    selectedJobIds,
    selectAll,
    deselectAll,
    toggleJob,
    selectJobsByIds,
    isSelected,
    selectedCount,
    hasSelection,
  };
}
